import { ShopAttributeDataType } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Badge,
  Button,
  Dialog,
  Input,
  Label,
  Select,
  SelectItem,
} from '@utima/ui';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useFeedAttributeMutation } from '@/features/feeds/useFeedAttributeMutation';
import { useEnumTranslations } from '@/hooks';
import { trpc } from '@/services/trpc';

import {
  type VocabularyMatchState,
  canAddValue,
  findVocabularyMatch,
  parseComparableNumber,
  resolveMatchState,
  resolveValueTyping,
} from '../attributeEditing';
import { useShopProductAttributeValueMutation } from '../useShopProductAttributeValueMutation';

type AttributeDraft = {
  name: string;
  displayName: string;
  dataType: ShopAttributeDataType;
  unit: string;
  baseUnit: string;
};

const emptyDraft: AttributeDraft = {
  name: '',
  displayName: '',
  dataType: ShopAttributeDataType.Text,
  unit: '',
  baseUnit: '',
};

/** The types an admin may pick. Tags are enrichment's own field — see below. */
const CREATABLE_DATA_TYPES = [
  ShopAttributeDataType.Text,
  ShopAttributeDataType.Number,
  ShopAttributeDataType.Boolean,
];

type VocabularyMatchNoticeProps = {
  state: VocabularyMatchState;
  label: string;
  isReactivating: boolean;
  onReactivate: () => void;
};

/**
 * What happens to a name the feed already knows, said before the save rather
 * than after it. A deactivated attribute is the case worth an extra button:
 * the value would be written and then shown nowhere, so the admin is offered
 * the feed-wide switch instead — named as feed-wide, because that is what it
 * is.
 */
const VocabularyMatchNotice = memo(function VocabularyMatchNotice({
  state,
  label,
  isReactivating,
  onReactivate,
}: VocabularyMatchNoticeProps) {
  const { t } = useLingui();

  return (
    <div className='flex flex-col gap-2 rounded-md border border-dashed border-border p-3'>
      {state === 'reuse' && (
        <span className='text-xs text-muted-fg'>
          {t`This feed already has "${label}", so the value is added to that attribute instead of creating a second one. Its own type and units apply.`}
        </span>
      )}

      {state === 'taken' && (
        <span className='text-xs text-warning'>
          {t`This product already has a value for "${label}". Edit the value it has instead of adding a second one.`}
        </span>
      )}

      {state === 'inactive' && (
        <>
          <span className='text-xs text-warning'>
            {t`"${label}" already exists but is switched off for the whole feed, so a value added to it would show nowhere.`}
          </span>
          <Button
            data-cta='admin-product-attribute-reactivate-button'
            variant='ghost'
            outline
            size='sm'
            className='w-fit'
            loading={isReactivating}
            onClick={onReactivate}
          >
            <Trans>Switch back on for the whole feed</Trans>
          </Button>
        </>
      )}
    </div>
  );
});

type NewAttributeFieldsProps = {
  draft: AttributeDraft;
  matchState: VocabularyMatchState;
  matchLabel: string;
  isNumeric: boolean;
  isPending: boolean;
  isReactivating: boolean;
  onChange: (patch: Partial<AttributeDraft>) => void;
  onReactivate: () => void;
};

/**
 * The definition half, and the one part of this dialog that is not about a
 * single product: what is created here joins the feed's vocabulary, is offered
 * to the enrichment model for the rest of the catalog, and catches a matching
 * merchant parameter on the next import instead of letting it open a second
 * row.
 *
 * The typing fields disappear as soon as the name resolves onto an attribute
 * that already exists, because that attribute keeps its own typing — nothing
 * typed here could change it, and offering fields that would be ignored is
 * worse than offering none.
 */
const NewAttributeFields = memo(function NewAttributeFields({
  draft,
  matchState,
  matchLabel,
  isNumeric,
  isPending,
  isReactivating,
  onChange,
  onReactivate,
}: NewAttributeFieldsProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();

  return (
    <div className='flex flex-col gap-4'>
      <p className='text-xs text-muted-fg'>
        <Trans>
          A new attribute joins the vocabulary this feed reuses, so the
          enrichment model is told to look for it across the whole catalog. Only
          the value below is added to this product.
        </Trans>
      </p>

      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='attribute-add-display-name'>
          <Trans>Display name</Trans>
        </Label>
        <Input
          id='attribute-add-display-name'
          value={draft.displayName}
          placeholder={t`Shown to people reviewing the catalog`}
          disabled={isPending}
          onChange={event => onChange({ displayName: event.target.value })}
        />
      </div>

      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='attribute-add-name'>
          <Trans>Canonical name</Trans>
        </Label>
        <Input
          id='attribute-add-name'
          value={draft.name}
          disabled={isPending}
          onChange={event => onChange({ name: event.target.value })}
        />
        <span className='text-xs text-muted-fg'>
          <Trans>
            Saved as a slug in the language of the feed — lowercase, no accents,
            underscores instead of spaces.
          </Trans>
        </span>
      </div>

      {matchState === 'none' ? (
        <>
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='attribute-add-data-type'>
              <Trans>Type</Trans>
            </Label>
            <Select
              id='attribute-add-data-type'
              value={draft.dataType}
              onValueChange={next =>
                onChange({ dataType: next as ShopAttributeDataType })
              }
            >
              {CREATABLE_DATA_TYPES.map(dataType => (
                <SelectItem key={dataType} value={dataType}>
                  {enumTranslations.shopAttributeDataType[dataType]}
                </SelectItem>
              ))}
            </Select>
          </div>

          {isNumeric && (
            <div className='flex flex-wrap items-start gap-3'>
              <div className='flex w-40 flex-col gap-1.5'>
                <Label htmlFor='attribute-add-unit'>
                  <Trans>Unit</Trans>
                </Label>
                <Input
                  id='attribute-add-unit'
                  value={draft.unit}
                  disabled={isPending}
                  onChange={event => onChange({ unit: event.target.value })}
                />
                <span className='text-xs text-muted-fg'>
                  <Trans>How the catalog writes it, like ml. Optional.</Trans>
                </span>
              </div>

              <div className='flex w-40 flex-col gap-1.5'>
                <Label htmlFor='attribute-add-base-unit'>
                  <Trans>Compared in</Trans>
                </Label>
                <Input
                  id='attribute-add-base-unit'
                  value={draft.baseUnit}
                  disabled={isPending}
                  onChange={event => onChange({ baseUnit: event.target.value })}
                />
                <span className='text-xs text-muted-fg'>
                  <Trans>
                    Every number on this attribute is stored in this unit, which
                    is what makes products comparable. Required.
                  </Trans>
                </span>
              </div>
            </div>
          )}
        </>
      ) : (
        <VocabularyMatchNotice
          state={matchState}
          label={matchLabel}
          isReactivating={isReactivating}
          onReactivate={onReactivate}
        />
      )}
    </div>
  );
});

type ValueFieldsProps = {
  value: string;
  numericValue: string;
  isNumeric: boolean;
  isPending: boolean;
  unitLabel: string;
  dataTypeLabel?: string;
  onValueChange: (value: string) => void;
  onNumericValueChange: (numericValue: string) => void;
};

/**
 * The value itself, in the two fields the edit row on the product already
 * uses: the text a shopper reads, unit included ("500 ml"), and the separate
 * number range filters compare, in the attribute's base unit.
 *
 * They are two fields rather than one field and a unit dropdown because the
 * unit lives *inside* the value text — that is what the enrichment prompt asks
 * the model for, and it is the only place the shape of `value` is written
 * down. The base unit is rendered beside the number as `(l)` rather than
 * appended to it: a label prevents a wrong scale, a widget that silently
 * appends units would cause one.
 */
const ValueFields = memo(function ValueFields({
  value,
  numericValue,
  isNumeric,
  isPending,
  unitLabel,
  dataTypeLabel,
  onValueChange,
  onNumericValueChange,
}: ValueFieldsProps) {
  return (
    <>
      <div className='flex flex-col gap-1.5'>
        <div className='flex flex-wrap items-center gap-1.5'>
          <Label htmlFor='attribute-add-value'>
            <Trans>Shown to shoppers</Trans>
          </Label>
          {dataTypeLabel && (
            <Badge size='xs' outline variant='muted'>
              {dataTypeLabel}
            </Badge>
          )}
        </div>
        <Input
          id='attribute-add-value'
          value={value}
          disabled={isPending}
          onChange={event => onValueChange(event.target.value)}
        />
      </div>

      {isNumeric && (
        <div className='flex flex-col gap-1.5'>
          <Label htmlFor='attribute-add-numeric-value'>
            <Trans>Compared as</Trans>
          </Label>
          <div className='flex items-center gap-1.5'>
            <Input
              id='attribute-add-numeric-value'
              className='w-40'
              inputMode='decimal'
              value={numericValue}
              disabled={isPending}
              onChange={event => onNumericValueChange(event.target.value)}
            />
            {unitLabel && (
              <span className='shrink-0 text-sm font-medium text-muted-fg'>
                ({unitLabel})
              </span>
            )}
          </div>
          <span className='text-xs text-muted-fg'>
            <Trans>
              The number is what range filters compare. Leave it empty if this
              product should not be compared on this attribute.
            </Trans>
          </span>
        </div>
      )}
    </>
  );
});

export type AddAttributeValueDialogProps = {
  open: boolean;
  productId: string;
  feedId: string;
  /**
   * Attributes this product already carries a row for, active or removed. One
   * row per product and attribute, so these are not offered again — the
   * existing row is edited or restored in the list instead.
   */
  takenAttributeIds: string[];
  onClose: () => void;
};

/**
 * Adds an attribute value the feed never carried and the enrichment model
 * never extracted — the counterpart of the correction and the removal the
 * product's attribute list already offers, and a `Manual` row like both of
 * them, which is what carries it past both writers: the importer only ever
 * updates and sweeps its own `Feed` rows, and enrichment's wholesale rewrite
 * stops at `Manual`.
 *
 * Two flavours, separated by scope rather than by placement: attaching an
 * attribute the feed already knows touches this product alone, while creating
 * one also adds an entry to the feed's vocabulary that the enrichment model is
 * told to reuse across the whole catalog.
 *
 * A typed name the feed already has — as a canonical name or as an alias — is
 * resolved onto that attribute rather than opening a second row for it, and
 * the form says so before the admin submits. The server resolves it too, so a
 * race cannot produce a duplicate: the form makes the outcome predictable, the
 * server is what makes it safe.
 */
export const AddAttributeValueDialog = memo(function AddAttributeValueDialog({
  open,
  productId,
  feedId,
  takenAttributeIds,
  onClose,
}: AddAttributeValueDialogProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const { createValue, createWithValue } =
    useShopProductAttributeValueMutation();
  const { setActive, update } = useFeedAttributeMutation();

  const { data: vocabulary } = trpc.shopProductAttributes.listByFeed.useQuery(
    { feedId },
    { enabled: open },
  );

  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [attributeId, setAttributeId] = useState('');
  const [draft, setDraft] = useState<AttributeDraft>(emptyDraft);
  const [value, setValue] = useState('');

  /**
   * Held as the raw string the admin typed rather than a number, so an empty
   * field stays empty instead of collapsing to 0. Empty is a real answer: it
   * means this row is not comparable to other products.
   */
  const [numericValue, setNumericValue] = useState('');

  useEffect(() => {
    setMode('existing');
    setAttributeId('');
    setDraft(emptyDraft);
    setValue('');
    setNumericValue('');
  }, [open, productId]);

  const updateDraft = useCallback(
    (patch: Partial<AttributeDraft>) =>
      setDraft(current => ({ ...current, ...patch })),
    [],
  );

  const taken = useMemo(() => new Set(takenAttributeIds), [takenAttributeIds]);

  const candidates = useMemo(
    () =>
      (vocabulary ?? []).filter(
        attribute => attribute.active && !taken.has(attribute.id),
      ),
    [vocabulary, taken],
  );

  const selected = useMemo(
    () => candidates.find(candidate => candidate.id === attributeId),
    [candidates, attributeId],
  );

  /**
   * A switched-off attribute is left out of the list rather than offered and
   * refused, but its absence is said out loud: silently omitting it would look
   * like the vocabulary never had it.
   */
  const hasHiddenInactive = useMemo(
    () =>
      (vocabulary ?? []).some(
        attribute => !attribute.active && !taken.has(attribute.id),
      ),
    [vocabulary, taken],
  );

  const match = useMemo(
    () =>
      mode === 'new'
        ? findVocabularyMatch({
            name: draft.name,
            attributes: vocabulary ?? [],
          })
        : undefined,
    [mode, draft.name, vocabulary],
  );

  const matchState = useMemo(
    () => resolveMatchState({ match, takenAttributeIds: taken }),
    [match, taken],
  );

  /** The definition a value would land under, once the name is resolved. */
  const target = mode === 'existing' ? selected : match;
  const targetLabel = target ? (target.displayName ?? target.name) : '';

  const { isNumeric, unitLabel } = resolveValueTyping({
    target,
    draftDataType: draft.dataType,
    draftBaseUnit: draft.baseUnit,
  });

  const comparable = parseComparableNumber(numericValue);
  const isPending = createValue.isPending || createWithValue.isPending;

  const canSubmit =
    canAddValue({
      mode,
      hasSelectedAttribute: !!selected,
      matchState,
      draftName: draft.name,
      draftBaseUnit: draft.baseUnit,
      isNumeric,
      value,
      numericValue,
    }) &&
    !isPending &&
    !update.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    const targetId = mode === 'existing' ? attributeId : match?.id;

    /**
     * The number is omitted entirely for a non-numeric attribute: the form
     * shows none for those, so it has no business sending one.
     */
    const numericPayload = isNumeric
      ? { numericValue: comparable.value }
      : undefined;

    await (targetId
      ? createValue.mutateAsync({
          product: productId,
          attribute: targetId,
          value: value.trim(),
          ...numericPayload,
        })
      : createWithValue.mutateAsync({
          product: productId,
          value: value.trim(),
          ...numericPayload,
          attribute: {
            name: draft.name.trim(),
            displayName: draft.displayName.trim() || null,
            dataType: draft.dataType,
            ...(isNumeric
              ? {
                  unit: draft.unit.trim() || null,
                  baseUnit: draft.baseUnit.trim(),
                }
              : {}),
          },
        }));

    onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onClose}>
      <Dialog.Content className='w-full max-w-[560px]'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Add a value to this product</Trans>
          </Dialog.Title>
          <Dialog.Description>
            <Trans>
              The value is added to this product alone, and is marked as edited
              by hand, so the next import and the next enrichment run both leave
              it alone.
            </Trans>
          </Dialog.Description>
        </Dialog.Header>

        <div className='flex flex-col gap-4 py-2'>
          <div className='flex gap-1 rounded-md border border-border p-1'>
            <Button
              size='sm'
              className='flex-1'
              variant={mode === 'existing' ? 'primary' : 'ghost'}
              aria-pressed={mode === 'existing'}
              onClick={() => setMode('existing')}
            >
              <Trans>Use an existing attribute</Trans>
            </Button>
            <Button
              size='sm'
              className='flex-1'
              variant={mode === 'new' ? 'primary' : 'ghost'}
              aria-pressed={mode === 'new'}
              onClick={() => setMode('new')}
            >
              <Trans>Create a new attribute</Trans>
            </Button>
          </div>

          {mode === 'existing' ? (
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='attribute-add-target'>
                <Trans>Attribute</Trans>
              </Label>
              <Select
                id='attribute-add-target'
                value={attributeId}
                onValueChange={setAttributeId}
                placeholder={t`Pick an attribute`}
              >
                {candidates.map(candidate => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    {candidate.displayName ?? candidate.name}
                  </SelectItem>
                ))}
              </Select>
              {candidates.length === 0 && (
                <span className='text-xs text-muted-fg'>
                  {vocabulary?.length ? (
                    <Trans>
                      Every attribute switched on for this feed already has a
                      value on this product.
                    </Trans>
                  ) : (
                    <Trans>
                      This feed has no attributes yet — create one to give this
                      product its first value.
                    </Trans>
                  )}
                </span>
              )}
              {hasHiddenInactive && (
                <span className='text-xs text-muted-fg'>
                  <Trans>
                    Attributes switched off for this feed are not listed here.
                    Switch one back on for the whole feed to add values to it
                    again.
                  </Trans>
                </span>
              )}
            </div>
          ) : (
            <NewAttributeFields
              draft={draft}
              matchState={matchState}
              matchLabel={targetLabel}
              isNumeric={isNumeric}
              isPending={isPending}
              isReactivating={update.isPending}
              onChange={updateDraft}
              onReactivate={() =>
                match && setActive({ id: match.id, active: true })
              }
            />
          )}

          <ValueFields
            value={value}
            numericValue={numericValue}
            isNumeric={isNumeric}
            isPending={isPending}
            unitLabel={unitLabel}
            dataTypeLabel={
              target
                ? enumTranslations.shopAttributeDataType[target.dataType]
                : undefined
            }
            onValueChange={setValue}
            onNumericValueChange={setNumericValue}
          />
        </div>

        <Dialog.Footer>
          <Button variant='secondary' onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            data-cta='admin-product-attribute-add-submit-button'
            variant='primary'
            loading={isPending}
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            <Trans>Add</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
});
